import { create } from 'zustand';
import type { ActiveRoute } from '@/hooks/useMapboxDirections';

// ─── Inline types (will be refactored to shared imports later) ───

interface Viewport {
  longitude: number;
  latitude: number;
  zoom: number;
  bearing: number;
  pitch: number;
}

type MapMode = 'venues' | 'events' | 'activities';

interface MapBounds {
  ne: [number, number]; // [lng, lat]
  sw: [number, number]; // [lng, lat]
}

// ─── Search result venue (fields needed for map pins + enriched list cards) ───
export interface SearchVenue {
  id: number;
  name: string;
  slug: string;
  latitude: number;
  longitude: number;
  venue_type: string;
  rating: number | null;
  review_count: number;
  // Enrichment — for list card display
  thumbnail_url: string | null;
  wifi_speed_mbps: number | null;
  has_wifi: boolean;
  wifi_tier: 'gold' | 'silver' | 'bronze' | null;
  tambon: string;
  hl_price_level: number | null;
  // Attribute icons
  dogs_allowed: boolean;
  good_for_kids: boolean;
  is_quiet: boolean;
  // Baht price range (e.g. "฿100-200")
  price_range: string | null;
}

/** Available Mapbox map styles */
export type MapStyleId = 'light' | 'streets' | 'outdoors' | 'dark' | 'satellite' | 'satellite-streets';

export interface MapStyleOption {
  id: MapStyleId;
  label: string;
  url: string;
}

export const MAP_STYLES: MapStyleOption[] = [
  { id: 'light', label: 'Light', url: 'mapbox://styles/mapbox/light-v11' },
  { id: 'streets', label: 'Streets', url: 'mapbox://styles/mapbox/streets-v12' },
  { id: 'outdoors', label: 'Outdoors', url: 'mapbox://styles/mapbox/outdoors-v12' },
  { id: 'dark', label: 'Dark', url: 'mapbox://styles/mapbox/dark-v11' },
  { id: 'satellite', label: 'Satellite', url: 'mapbox://styles/mapbox/satellite-v9' },
  { id: 'satellite-streets', label: 'Hybrid', url: 'mapbox://styles/mapbox/satellite-streets-v12' },
];

/** Read saved map style from localStorage */
function getSavedMapStyle(): MapStyleId {
  try {
    const saved = localStorage.getItem('hl_map_style');
    if (saved && MAP_STYLES.some((s) => s.id === saved)) return saved as MapStyleId;
  } catch { /* ignore */ }
  return 'streets';
}

interface MapStore {
  // Viewport
  viewport: Viewport;
  setViewport: (v: Partial<Viewport>) => void;

  // Bounds (updated on every map move/zoom)
  bounds: MapBounds | null;
  setBounds: (b: MapBounds | null) => void;

  // Interaction state
  hoveredVenueId: number | null;
  selectedVenueId: number | null;
  setHovered: (id: number | null) => void;
  setSelected: (id: number | null) => void;

  // v68.189 — Hovered amenity type for the selected venue. When the user
  // hovers (desktop) or taps (mobile) an amenity chip in VenueSheet, set
  // this to e.g. 'pool' / 'spa' / 'gym'. MapCanvas renders matching
  // polygons from agents.venue_amenity_polygons with full opacity.
  hoveredAmenityType: string | null;
  setHoveredAmenityType: (t: string | null) => void;

  // GPS-based initial position
  hasLocated: boolean;
  setHasLocated: () => void;

  // Cached last-known user GPS [lng, lat] — used to re-center the map on the
  // user's current location on every transition (mode switch, return-from-venue,
  // hard refresh) without waiting for a fresh GPS fix each time. Patrick 2026-06-08.
  userPos: [number, number] | null;
  setUserPos: (p: [number, number] | null) => void;

  // Mode toggle — venues vs events on same map canvas
  mode: MapMode;
  setMode: (m: MapMode) => void;

  // Map style (user-selectable, persisted in localStorage)
  mapStyleId: MapStyleId;
  mapStyleUrl: string;
  setMapStyle: (id: MapStyleId) => void;

  // Map API callbacks (registered by MapCanvas on load)
  mapFlyTo: ((center: [number, number], zoom: number, duration?: number) => void) | null;
  mapFitBounds: ((sw: [number, number], ne: [number, number], padding?: number) => void) | null;
  setMapCallbacks: (
    flyTo: (center: [number, number], zoom: number, duration?: number) => void,
    fitBounds: (sw: [number, number], ne: [number, number], padding?: number) => void,
  ) => void;

  // Search results mode (desktop: numbered pins + left-panel list)
  searchResults: SearchVenue[] | null;  // null = browse mode
  searchQuery: string;
  activeSearchResultId: number | null;  // hovered result
  setSearchResults: (venues: SearchVenue[], query: string) => void;
  clearSearch: () => void;
  setActiveSearchResult: (id: number | null) => void;

  // Active route (set when user picks a transport mode in venue sheet)
  activeRoute:     ActiveRoute | null;
  setActiveRoute:  (route: ActiveRoute | null) => void;

  // EV-605 round 6 — current bottom inset (px) covered by the venue sheet.
  // Map fits (routes, boundaries, show-on-map) treat only the strip ABOVE
  // this as the visible map, and refit when it changes (sheet dragged).
  sheetInset: number;
  setSheetInset: (px: number) => void;

  // EV-524 P5 — the consumer 🏷 offers map layer toggle
  showOffers: boolean;
  /** v70.364 — the rows behind the 🏷️ offer pins, so the list can render them. */
  offerVenues: any[];
  setOfferVenues: (v: any[]) => void;
  setShowOffers: (v: boolean) => void;
  // EV-1299 — 🛵 vendor-ONLY filter: when true the map shows ONLY live mobile street vendors
  // (regular venues hidden). The live-vendor pins themselves always render as an overlay.
  vendorOnly: boolean;
  setVendorOnly: (v: boolean) => void;

  // Boundary overlay toggle (admin boundary polygons)
  showBoundaries: boolean;
  setShowBoundaries: (v: boolean) => void;

  // Area filter (tambon multi-select)
  selectedAreas: string[];
  setSelectedAreas: (areas: string[]) => void;

  // Multi-region switcher
  currentRegion: string;
  setCurrentRegion: (region: string) => void;

  // Hovered area name (set by dropdown hover, read by BoundaryLayer for highlight)
  hoveredAreaName: string | null;
  setHoveredAreaName: (name: string | null) => void;
}

// ─── Defaults ───

/** Default viewport — region-aware.
 *  On vn.honestlee.app ALWAYS start in Da Nang (ignore stale saved coords from
 *  another region). On thai.honestlee.app, prefer last saved else Phuket.
 *  v68.164 — fixes "map starts in Phuket when Patrick opens vn.honestlee.app". */
const host = typeof window !== 'undefined' ? window.location.hostname : '';
const isVN = /vn\.honestlee\.app$/i.test(host);

const savedLat = parseFloat(localStorage.getItem('hl_last_lat') || '');
const savedLng = parseFloat(localStorage.getItem('hl_last_lng') || '');

// Da Nang: lat 16.05, lng 108.22. On vn host, only honor saved coords if they're
// already inside the Vietnam box — otherwise reset to Da Nang.
const savedInVN = !isNaN(savedLat) && !isNaN(savedLng) &&
                  savedLat > 8 && savedLat < 24 && savedLng > 102 && savedLng < 110;

const DEFAULT_VIEWPORT: Viewport = {
  longitude: (isVN ? (savedInVN ? savedLng : 108.2208) : (!isNaN(savedLng) ? savedLng : 108.2208)),
  latitude:  (isVN ? (savedInVN ? savedLat : 16.0544)  : (!isNaN(savedLat) ? savedLat : 16.0544)),
  zoom: 14,
  bearing: 0,
  pitch: 0,
};

// ─── Store ───

export const useMapStore = create<MapStore>((set) => ({
  viewport: DEFAULT_VIEWPORT,
  setViewport: (v) =>
    set((s) => ({ viewport: { ...s.viewport, ...v } })),

  bounds: null,
  setBounds: (bounds) => set({ bounds }),

  hoveredVenueId: null,
  selectedVenueId: null,
  setHovered: (hoveredVenueId) => set({ hoveredVenueId }),
  setSelected: (selectedVenueId) => set({ selectedVenueId }),

  // v68.189 — amenity hover state
  hoveredAmenityType: null,
  setHoveredAmenityType: (hoveredAmenityType) => set({ hoveredAmenityType }),

  hasLocated: false,
  setHasLocated: () => set({ hasLocated: true }),

  userPos: null,
  setUserPos: (userPos) => set({ userPos }),

  mode: 'venues',
  setMode: (mode) => set({ mode }),

  mapStyleId: getSavedMapStyle(),
  mapStyleUrl: MAP_STYLES.find((s) => s.id === getSavedMapStyle())?.url ?? MAP_STYLES[0].url,
  setMapStyle: (id) => {
    const style = MAP_STYLES.find((s) => s.id === id);
    if (!style) return;
    try { localStorage.setItem('hl_map_style', id); } catch { /* ignore */ }
    set({ mapStyleId: id, mapStyleUrl: style.url });
  },

  mapFlyTo: null,
  mapFitBounds: null,
  setMapCallbacks: (flyTo, fitBounds) => set({ mapFlyTo: flyTo, mapFitBounds: fitBounds }),

  searchResults: null,
  searchQuery: '',
  activeSearchResultId: null,

  setSearchResults: (venues, query) => {
    // Store results — fitBounds is triggered by MapPage useEffect after render
    set({ searchResults: venues, searchQuery: query, activeSearchResultId: null });
  },

  clearSearch: () => set({ searchResults: null, searchQuery: '', activeSearchResultId: null }),

  setActiveSearchResult: (id) => {
    // Map movement (flyTo / fitBounds) is handled by MapCanvas useEffect watching this value
    set({ activeSearchResultId: id });
  },

  activeRoute: null,
  setActiveRoute: (activeRoute) => set({ activeRoute }),

  sheetInset: 0,
  setSheetInset: (sheetInset) => set((st) => st.sheetInset === sheetInset ? st : { sheetInset }),

  showOffers: false,
  offerVenues: [],
  setOfferVenues: (offerVenues) => set({ offerVenues }),
  setShowOffers: (showOffers) => set({ showOffers }),
  vendorOnly: false,
  setVendorOnly: (vendorOnly) => set({ vendorOnly }),

  showBoundaries: false,
  setShowBoundaries: (showBoundaries) => set({ showBoundaries }),

  selectedAreas: [],
  setSelectedAreas: (selectedAreas) => set({ selectedAreas }),

  // Default region: detect from hostname (vn.* → danang-hoian) before falling back to localStorage
  // Patrick 2026-04-25: vn.honestlee.app must default to Da Nang, not Phuket
  // Region slugs match AreaFilter.tsx: 'danang-hoian', 'phuket', etc.
  // ALSO: auto-migrate stale TH localStorage on vn.* hosts (visitors who came from old thai.*).
  currentRegion: (() => {
    const host = typeof window !== 'undefined' ? window.location.hostname : '';
    // CON-1504 — Patrick 2026-08-02: "if I go to Bangkok or Krabi and click on map,
    // it goes nowhere. And that's for any other links in the gallery there."
    // The region was derived ONLY from the hostname, so every Thailand link opened on
    // the Vietnam host and resolved to nothing. An explicit ?region= override makes a
    // cross-region deep link work on ANY host — including stg.honestlee.app, which has
    // no Thai sibling and must never bounce the user into production.
    const VALID = ['danang-hoian', 'phuket', 'krabi', 'phangnga', 'bangkok', 'chiangmai'];
    const forced = typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search).get('region') : null;
    if (forced && VALID.includes(forced)) {
      try { localStorage.setItem('hl_v2_region', forced); } catch { /* private mode */ }
      return forced;
    }
    const isVnHost = host.includes('vn.honestlee.app') || host.includes('honestlee.vn');
    const isThHost = host.includes('thai.honestlee.app') || host.includes('th.honestlee.app');
    const hostDefault = isVnHost ? 'danang-hoian' : isThHost ? 'phuket' : 'danang-hoian';
    let saved = typeof localStorage !== 'undefined' ? localStorage.getItem('hl_v2_region') : null;

    // Migrate: if on vn.* host but localStorage has a TH region from a prior thai.* visit, reset.
    const TH_REGIONS = ['phuket', 'krabi', 'phangnga', 'bangkok', 'chiangmai'];
    if (isVnHost && saved && TH_REGIONS.includes(saved)) {
      try {
        localStorage.removeItem('hl_v2_region');
        localStorage.removeItem('hl_last_lat');
        localStorage.removeItem('hl_last_lng');
      } catch {}
      saved = null;
    }
    return saved || hostDefault;
  })(),
  setCurrentRegion: (currentRegion) => {
    localStorage.setItem('hl_v2_region', currentRegion);
    set({ currentRegion, selectedAreas: [] });
  },

  hoveredAreaName: null,
  setHoveredAreaName: (hoveredAreaName) => set({ hoveredAreaName }),
}));
